/**
 * Tight bounding box for the visible “content” of a sprite sheet.
 * Handles (1) true transparency, (2) flat solid margins (white/black) common in PNG exports.
 *
 * Returns pixel bounds in image space: left, top inclusive; right, bottom exclusive.
 */
export function getTightAlphaBoundsFromImage(img) {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (!w || !h) return null;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0);

  let data;
  try {
    data = ctx.getImageData(0, 0, w, h).data;
  } catch {
    return { left: 0, top: 0, right: w, bottom: h };
  }

  const at = (x, y) => {
    const i = (y * w + x) * 4;
    return [data[i], data[i + 1], data[i + 2], data[i + 3]];
  };

  const avgCornerBg = () => {
    const pts = [
      [0, 0],
      [w - 1, 0],
      [0, h - 1],
      [w - 1, h - 1]
    ];
    let r = 0;
    let g = 0;
    let b = 0;
    for (const [x, y] of pts) {
      const [rr, gg, bb] = at(x, y);
      r += rr;
      g += gg;
      b += bb;
    }
    return [r / 4, g / 4, b / 4];
  };

  const [bgR, bgG, bgB] = avgCornerBg();

  const colorDistSq = (r, g, b) => {
    const dr = r - bgR;
    const dg = g - bgG;
    const db = b - bgB;
    return dr * dr + dg * dg + db * db;
  };

  /** Pixel belongs to the character / foreground, not empty plate background. */
  const isForeground = (r, g, b, a) => {
    const opaqueBg = a > 248;
    const transparent = a < 12;
    if (transparent) return false;

    const distSq = colorDistSq(r, g, b);
    // Different from flat margin color (opaque white/black band around sprites)
    if (distSq > 38 * 38) return true;
    // Soft alpha fringe (anti-alias, glow) — keep as part of subject
    if (a < 245) return true;
    // Slight color shift on “empty” areas that aren’t exactly corner (jpeg-ish)
    if (opaqueBg && distSq > 22 * 22) return true;
    return false;
  };

  let minX = w;
  let minY = h;
  let maxX = 0;
  let maxY = 0;
  let found = false;

  for (let y = 0; y < h; y++) {
    const row = y * w * 4;
    for (let x = 0; x < w; x++) {
      const i = row + x * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      if (!isForeground(r, g, b, a)) continue;
      found = true;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }

  if (!found) {
    return { left: 0, top: 0, right: w, bottom: h };
  }

  return { left: minX, top: minY, right: maxX + 1, bottom: maxY + 1 };
}
